import { call, put, select, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import isNaN from 'lodash/isNaN';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import each from 'lodash/each';
import { message } from 'antd';
import { api } from '$app/api';
import takeOneAndBlock from '$app/utils/takeOneAndBlock';
import { poundsToPence, penceToPounds } from '$app/utils/util';
import { BOOKING_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';
import {
  bookingQuoteSelector,
  jobInvitationSagaSelector,
  getBookingJobInvitationIdSelector,
  bookingIdSelector,
  bookingMasterStatusIDSelector,
  bookingGarageInviteSelector,
  bookingAcceptedGarageSelector,
  proposedQuoteIdSelector,
  getAuthUser,
} from './selectors';
import {
  closePopupAction,
  getCampaignCodeError,
  workDetailsError,
  loadQuoteSuccessAction,
  savingRowAction,
  // deletingRowAction,
  getGarageWorkOperationPricesSuccess,
  getGarageWorkOperationPricesError,
  getOriginalWorkOperationPricesSuccess,
  getOriginalWorkOperationPricesError,
  finaliseProposedWorkOperationsSuccess,
  finaliseProposedWorkOperationsError,
  getGarageWorkOperationPrices,
  getOriginalWorkOperationPrices,
} from './actions';
import { bookingLoaded, patchBookingAction, addNoteAction, reloadBooking as reloadBookingAction } from '../actions';
import {
  LINE_ITEMS_PRICE_COLUMNS,
  VALIDATE_CAMPAIGN_ACTION,
  EDIT_LINE_ITEM,
  DELETE_LINE_ITEM,
  SAVE_WORK_OPERATION,
  DELETE_WORK_OPERATION,
  SEND_JOB_INVITE,
  GARAGE_WORK_OPERATION_PRICES,
  ORIGINAL_WORK_OPERATION_PRICES,
  FINALISE_PROPOSED_WORK_OPERATION,
  SEND_QUOTE_TO_CUSTOMER,
  UPDATE_TO_CUSTOMER_REVIEW,
  ADJUSTMENT_TYPE,
  DRIVER_FEE,
} from './constants';
import {
  approvedProposedWorkOperationsSagaSelector,
  declinedProposedWorkOperationsSagaSelector,
} from './selectors/quoteWorkOperationsSelector';
import { LOAD_BOOKING_SUCCESS, JOB_INVITE_SUCCESS } from '../constants';

import { getAccountingCodesByType, removeVAT, applyVAT } from './util';

import { getJobInviteSentBy } from '../../../shared/getJobInviter';
import config from '$config';
import { getSelectedJobInvite, getJobInviteIdFromUrl } from '../utils';

function* getCampaignCode(props) {
  const { campaignCode } = props.code;
  try {
    const { data } = yield call(api, `coupons/${campaignCode}`, {
      method: 'get',
      data: {},
    });
    yield put(closePopupAction());
    yield put(patchBookingAction({ campaignId: data.campaignId }));
  } catch (err) {
    yield put(getCampaignCodeError(err));
  }
}

function* dispatchLoadingActions(quote, jobInvitation, garage, bookingId) {
  yield put(loadQuoteSuccessAction(quote, jobInvitation, garage));
  if (garage) {
    yield put(getGarageWorkOperationPrices(bookingId, garage.garageId));
    yield put(getOriginalWorkOperationPrices(bookingId, garage.garageId));
  }
}

function* getInviteById(jobInviteIdToLoad) {
  return (yield call(api, `job-invitations/${jobInviteIdToLoad}`)).data;
}

function* loadSpecificQuote() {
  const bookingId = yield select(bookingIdSelector);
  const masterStatusId = yield select(bookingMasterStatusIDSelector);
  const garageInvites = yield select(bookingGarageInviteSelector);
  const bookingJobInviteId = yield select(getBookingJobInvitationIdSelector);

  const selectedJobInvitation = getSelectedJobInvite({
    masterStatusId,
    garageInvites,
  });

  if (selectedJobInvitation) {
    // if there is a Selected job invitation, use it to display the quote
    const { quote, garage } = yield getInviteById(selectedJobInvitation.invitationId);
    yield dispatchLoadingActions(quote, selectedJobInvitation, garage, bookingId);
  } else if (bookingJobInviteId) {
    // if there is a invitation id on the booking, use it to display the quote
    const invite = yield getInviteById(bookingJobInviteId);
    yield dispatchLoadingActions(invite.quote, invite, invite.garage, bookingId);
  } else {
    // use the default quote on the booking
    const bookingQuote = yield select(bookingQuoteSelector);
    yield dispatchLoadingActions(bookingQuote, null, null, bookingId);
  }
}

function* loadDefaultBookingQuote() {
  // docs
  // https://docs.google.com/document/d/1s3pQOMR7BcTraKQJ9VLx7R7KhCjOxF9Y8gQqW3qEYpA/edit
  const bookingQuote = yield select(bookingQuoteSelector);

  const bookingId = yield select(bookingIdSelector);
  const masterStatusId = yield select(bookingMasterStatusIDSelector);
  const garageInvites = yield select(bookingGarageInviteSelector);

  if (masterStatusId >= BOOKING_STATUS.ALLOCATED && masterStatusId !== BOOKING_STATUS.GARAGE_DECLINED) {
    const acceptedInvite = garageInvites.find((inv) => inv.status === 'accepted');
    const garage = yield select(bookingAcceptedGarageSelector);
    yield dispatchLoadingActions(bookingQuote, acceptedInvite, garage, bookingId);
  } else {
    const draftOrPendingInvites = garageInvites.filter(
      (invite) => invite.status === 'draft' || invite.status === 'pending'
    );

    const mostRecentInvite = draftOrPendingInvites.sort((a, b) =>
      a.creationDateTime > b.creationDateTime ? -1 : 1
    )[0];

    if (mostRecentInvite) {
      const { invitationId, garage } = mostRecentInvite;
      const { data } = yield call(api, `job-invitations/${invitationId}`);
      yield dispatchLoadingActions(data.quote, mostRecentInvite, garage, bookingId);
    } else {
      yield put(loadQuoteSuccessAction(bookingQuote, {}, null));
    }
  }
}

function* loadQuote() {
  const masterStatusId = yield select(bookingMasterStatusIDSelector);
  const selectedJobInviteIdFromUrl = getJobInviteIdFromUrl();
  const bookingIsNotAllocated = masterStatusId < BOOKING_STATUS.ALLOCATED;
  const useMultiInvite = bookingIsNotAllocated || selectedJobInviteIdFromUrl;

  return useMultiInvite ? yield loadSpecificQuote() : yield loadDefaultBookingQuote();
}

/**
 * @deprecated
 * @see deleteWorkOperation()
 */
function* deleteLineItem(action) {
  // yield put(deletingRowAction(true));
  const jobInvitation = yield select(jobInvitationSagaSelector);

  const quoteData = {
    remove: [action.operationId],
  };

  if (jobInvitation && jobInvitation.status && jobInvitation.status !== 'accepted') {
    yield* PatchQuoteOnJobInvitation(jobInvitation, quoteData);
  } else {
    yield* PatchQuoteOnBooking(quoteData);
  }
}

function* SendJobInvite(action) {
  const jobInvitation = yield select(jobInvitationSagaSelector);
  try {
    const dataToSend = {
      status: 'pending',
      notes: action.note,
    };

    const sentBy = getJobInviteSentBy({ status: 'pending' });
    if (sentBy !== undefined) dataToSend.sentBy = sentBy;

    yield call(api, `job-invitations/${jobInvitation.invitationId}`, {
      method: 'patch',
      data: dataToSend,
    });

    yield put(reloadBookingAction());
  } catch (err) {
    yield put(workDetailsError(err));
  }
}

function* PatchQuoteOnBooking(quoteData) {
  try {
    const bookingId = yield select(bookingIdSelector);
    const { data } = yield call(api, `/bookings/${bookingId}/quote`, {
      method: 'patch',
      data: quoteData,
    });
    yield put(bookingLoaded(data));
  } catch (err) {
    yield put(workDetailsError(err, quoteData));
  }
}

function* PatchQuoteOnJobInvitation(jobInvitation, quoteData) {
  try {
    const dataToSend = {
      status: jobInvitation.status || 'draft',
      quote: quoteData,
    };

    const sentBy = getJobInviteSentBy({ status: 'draft' });
    if (sentBy !== undefined) dataToSend.sentBy = sentBy;

    const { data } = yield call(api, `job-invitations/${jobInvitation.invitationId}`, {
      method: 'patch',
      data: dataToSend,
    });
    yield put(loadQuoteSuccessAction(data.quote, data, data.garage));
  } catch (err) {
    yield put(workDetailsError(err, quoteData));
  }
}

function* PatchProposedQuote(quoteData) {
  try {
    const bookingId = yield select(bookingIdSelector);
    const proposedQuoteId = yield select(proposedQuoteIdSelector);
    const { data } = yield call(api, `bookings/${bookingId}/proposed-work/${proposedQuoteId}`, {
      method: 'patch',
      data: quoteData,
    });
    yield put(bookingLoaded(data));
  } catch (err) {
    yield put(workDetailsError(err, quoteData));
  }
}

/**
 * @deprecated
 * @see saveWorkOperation()
 */
function* editQuote({ lineItem }) {
  const { operationId, ...data } = lineItem;
  yield put(savingRowAction(true));
  each(pick(data, LINE_ITEMS_PRICE_COLUMNS), (v, k) => (data[k] = poundsToPence(v)));
  const quoteData = {
    // @todo consider setting accounting codes from back-end instead of front-end
    add: [Object.assign(data, getAccountingCodesByType(data.type))],
    remove: operationId ? [operationId] : [],
  };
  const jobInvitation = yield select(jobInvitationSagaSelector);
  if (isEmpty(jobInvitation) || jobInvitation.status === 'accepted') {
    yield* PatchQuoteOnBooking(quoteData);
  } else {
    yield* PatchQuoteOnJobInvitation(jobInvitation, quoteData);
  }
  // hitKeyESC(); // make sure add new row dissappears
}

/**
 * Determine which quote to patch
 * @param {Object} quoteData
 */
function* PatchQuote(quoteData, quoteType = 'default') {
  if (quoteType === 'proposed') {
    yield* PatchProposedQuote(quoteData);
  } else {
    const jobInvitation = yield select(jobInvitationSagaSelector);
    if (isEmpty(jobInvitation) || jobInvitation.status === 'accepted') {
      yield* PatchQuoteOnBooking(quoteData);
    } else {
      yield* PatchQuoteOnJobInvitation(jobInvitation, quoteData);
    }
  }
}

function* deleteWorkOperation({ operationId, quoteType }) {
  yield* PatchQuote({ remove: [operationId] }, quoteType);
}

function* saveWorkOperation({ data }) {
  if (data.type === ADJUSTMENT_TYPE) {
    const { lineItems } = yield select(bookingQuoteSelector);
    const adjustmentOperation = lineItems.find((li) => li.type === ADJUSTMENT_TYPE);

    data.deleted = !isEmpty(adjustmentOperation) ? [adjustmentOperation.operationId] : [];
  }
  const isAdditionalDiscount = data.sku === 'additional-discount';
  const quoteData = prepareSaveWorkOperation(data);
  yield* PatchQuote(quoteData, get(data, 'quoteType'));
  if (isAdditionalDiscount) {
    message.success('Your coupon code has been added');
  }
}

const setLineItemPriceInPence = (lineItem) => {
  const li = { ...lineItem };
  LINE_ITEMS_PRICE_COLUMNS.forEach((k) => (li[k] = poundsToPence(li[k])));
  if (!li.fixedCostWithVAT) {
    li.fixedCostWithVAT = li.vatExempt ? li.fixedCost : Math.round(applyVAT(li.fixedCost));
  }
  return li;
};

const prepareSaveWorkOperation = ({
  key,
  operationId,
  lineItems,
  deleted = [],
  type,
  workStatus,
  quoteId,
  ...wo
}) => {
  const woType = type !== 'custom' ? type : 'other';
  /*
   * Warning!
   **********
   * TODO: This should be moved to our backend!!
   *
   * This kind of implicit type checking is leading to lots if bugs
   * https://docs.google.com/document/d/1nZ9CpyS6Ta9TtxN_9xMToN_q-yZXZg3zbXguSTKbvxw/edit?usp=sharing
   **
   */

  const { vatExempt } = wo;

  let accountingCodes =
    wo.sku === 'fixter-driver-fee' || wo.sku === 'garage-driver-fee'
      ? getAccountingCodesByType(DRIVER_FEE, wo)
      : getAccountingCodesByType(woType, wo);

  const prepareSubLineItem = (sli) => {
    if (woType === ADJUSTMENT_TYPE) {
      accountingCodes = {
        ...accountingCodes,
        accountCodePurchases: sli.accountCodePurchases,
      };
    }

    return Object.assign(omit(setSubLineItemPrice(sli), ['key', 'operationId', 'quantity']), {
      ...accountingCodes,
      type: woType,
      // for adjustment subLineItems, we must keep the vatExempt because in this case
      // each adjustment can have different accountCodePurchases, otherwise the total will be incorrect
      vatExempt: woType === ADJUSTMENT_TYPE ? sli.vatExempt : vatExempt || false,
    });
  };

  const add = omitBy(
    setLineItemPriceInPence({
      ...omit(wo, ['children', 'quantity']),
      ...accountingCodes,
      type: woType,
      workStatus,
      subLineItems: lineItems ? lineItems.map(prepareSubLineItem) : null,
    }),
    (v) => isNil(v) || isNaN(v)
  );
  add.fixedCost = add.vatExempt ? add.fixedCostWithVAT : Math.round(removeVAT(add.fixedCostWithVAT));
  return {
    quoteId,
    add: [add],
    remove: deleted.concat(key ? [key] : []),
  };
};

const calculateFixedCost = (vatExempt, fixedCost, fixedCostWithVat) => {
  if (fixedCost && isNumber(fixedCost)) return fixedCost;
  return vatExempt ? penceToPounds(fixedCostWithVat) : penceToPounds(Math.round(removeVAT(fixedCostWithVat)));
};

const setSubLineItemPrice = ({
  type,
  quantity,
  price,
  fixedCost,
  fixedCostWithVat,
  fixedCostWithVAT,
  vatExempt,
  metadata: m = {},
  ...data
}) => {
  let sli = {};
  switch (type) {
    case 'labour':
      sli = {
        ...data,
        labourTime: quantity,
        labourCost: price,
        partsQuantity: 0,
        partsPrice: 0,
        fixedCost: calculateFixedCost(
          vatExempt,
          fixedCost,
          isNumber(fixedCostWithVat) ? fixedCostWithVat : poundsToPence(fixedCostWithVAT)
        ),
        fixedCostWithVAT: fixedCostWithVAT || penceToPounds(fixedCostWithVat),
        metadata: {
          ...m,
          initialLabourTime: quantity,
          initialLabourCost: poundsToPence(price),
        },
      };
      break;
    case 'part':
      sli = {
        ...data,
        labourTime: 0,
        labourCost: 0,
        partsQuantity: quantity,
        partsPrice: price,
        fixedCost: calculateFixedCost(
          vatExempt,
          fixedCost,
          isNumber(fixedCostWithVat) ? fixedCostWithVat : poundsToPence(fixedCostWithVAT)
        ),
        fixedCostWithVAT: fixedCostWithVAT || penceToPounds(fixedCostWithVat),
        metadata: {
          ...m,
          initialPartsQuantity: quantity,
          initialPartsPrice: poundsToPence(price),
        },
      };
      break;
    case ADJUSTMENT_TYPE:
      return { ...data };
    default:
      break;
  }
  return setLineItemPriceInPence(sli);
};

function* loadGarageWorkOperationPrices({ bookingId, garageId }) {
  try {
    let data;
    let index = 0;
    while (!data && index < 20) {
      index += 1;
      try {
        const newResponse = yield call(api, `/bookings/${bookingId}/garages/${garageId}/prices`);
        data = newResponse.data;
      } catch (err) {
        data = null;
      }
    }

    if (!data) {
      throw Error('Failed to pull new pricelist!');
    }

    yield put(getGarageWorkOperationPricesSuccess(data));
  } catch (err) {
    yield put(getGarageWorkOperationPricesError(err));
  }
}

function* loadOriginalWorkOperationPrices({ bookingId, garageId }) {
  try {
    let data;
    let index = 0;
    while (!data && index < 20) {
      index += 1;
      try {
        const newResponse = yield call(
          api,
          `/bookings/${bookingId}/garages/${garageId}/prices?originalPriceList=true`
        );
        data = newResponse.data;
      } catch (err) {
        data = null;
      }
    }

    if (!data) {
      throw Error('Failed to pull new pricelist!');
    }

    yield put(getOriginalWorkOperationPricesSuccess(data));
  } catch (err) {
    yield put(getOriginalWorkOperationPricesError(err));
  }
}

function* finaliseProposedWorkOperations() {
  const { bookingId } = yield select(jobInvitationSagaSelector);
  const approvedProposedWorkOperations = yield select(approvedProposedWorkOperationsSagaSelector);
  const declinedProposedWorkOperations = yield select(declinedProposedWorkOperationsSagaSelector);

  const { name, surname } = yield select(getAuthUser());
  const caseManager = `${name} ${surname}`;

  try {
    const { data } = yield call(api, `/bookings/${bookingId}/proposed-work`, {
      method: 'patch',
      data: {
        approved: approvedProposedWorkOperations,
        declined: declinedProposedWorkOperations,
        reviewedBy: caseManager,
      },
    });
    yield put(patchBookingAction({ garageStatus: 'WORK_STARTED' }));
    yield put(finaliseProposedWorkOperationsSuccess(data));
  } catch (err) {
    yield put(workDetailsError(err));
    yield put(finaliseProposedWorkOperationsError(err));
  }
}

function* SendQuoteToCustomer() {
  const bookingId = yield select(bookingIdSelector);
  const { name } = yield select(getAuthUser());

  const errorMessage = 'Failed sending proposed quote to the customer';

  try {
    const { data } = yield call(api, `/bookings/${bookingId}/sendCustomerReviewNotification`, {
      method: 'post',
      data: {
        caseManager: name,
      },
    });

    if (data) {
      message.success('Proposed quote has been sent to the customer', 3);
    } else {
      message.error(errorMessage, 3);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err, errorMessage);
    message.error(errorMessage, 3);
  }
}

function* updateToCustomerReview() {
  yield put(patchBookingAction({ garageStatus: 'CUSTOMER_REVIEW' }));
  yield put(
    addNoteAction({
      content:
        'Booking transitioned to customer reviewing, no automatic notifications were sent.' +
        'Call customer to discuss proposed quote.',
    })
  );
}

export default function* workDetailsSaga() {
  yield takeLatest(VALIDATE_CAMPAIGN_ACTION, getCampaignCode);
  yield* takeOneAndBlock(SAVE_WORK_OPERATION, saveWorkOperation);
  yield* takeOneAndBlock(DELETE_WORK_OPERATION, deleteWorkOperation);
  yield* takeOneAndBlock(EDIT_LINE_ITEM, editQuote);
  yield* takeOneAndBlock(DELETE_LINE_ITEM, deleteLineItem);
  yield* takeOneAndBlock(SEND_JOB_INVITE, SendJobInvite);
  yield* takeOneAndBlock(SEND_QUOTE_TO_CUSTOMER, SendQuoteToCustomer);
  yield* takeOneAndBlock(UPDATE_TO_CUSTOMER_REVIEW, updateToCustomerReview);
  yield takeLatest(LOAD_BOOKING_SUCCESS, loadQuote);
  yield takeLatest(JOB_INVITE_SUCCESS, loadQuote);
  yield takeLatest(GARAGE_WORK_OPERATION_PRICES, loadGarageWorkOperationPrices);
  yield takeLatest(ORIGINAL_WORK_OPERATION_PRICES, loadOriginalWorkOperationPrices);
  yield* takeOneAndBlock(FINALISE_PROPOSED_WORK_OPERATION, finaliseProposedWorkOperations);
}
